/**
 * Every date Finance Buddy shows or stores is YYYY-MM-DD, and every timestamp
 * is YYYY-MM-DDTHH:MM:SSZ (UTC). This file is the only place either is turned
 * into text. See ARCHITECTURE.md, Dates.
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** True for a real calendar date in YYYY-MM-DD form (rejects 2026-02-30). */
export function isIsoDate(value: string): boolean {
  const m = ISO_DATE.exec(value)
  if (!m) return false
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const date = new Date(Date.UTC(y, mo - 1, d))
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d
}

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * How many months of data run from `from` to `to` (YYYY-MM-DD), both days
 * included, as a fraction and at least one. Counted in calendar months, so
 * 2026-04-16 to 2026-10-15 is exactly 6 (though it touches 7 calendar
 * months) and a year to the day is exactly 12. What a monthly average divides by.
 */
export function monthsOfData(from: string, to: string): number {
  if (!isIsoDate(from) || !isIsoDate(to) || to < from) return 1
  // Measured from the day before the first, so a range of whole months comes out whole.
  const start = new Date(Date.parse(from) - 86_400_000)
  const end = new Date(Date.parse(to))
  let months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + end.getUTCMonth() - start.getUTCMonth()
  let days = end.getUTCDate() - start.getUTCDate()
  if (days < 0) {
    months -= 1
    days += new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 0)).getUTCDate()
  }
  const inMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)).getUTCDate()
  return Math.max(1, months + days / inMonth)
}

/** A calendar date in the viewer's local time zone. */
export function formatDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** A UTC timestamp, to the second. */
export function formatTimestamp(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/** Days from 1899-12-30 (the Sheets epoch) to 1970-01-01. */
const SHEETS_EPOCH_OFFSET = 25569

/**
 * Reads a date cell leniently: a YYYY-MM-DD string, a timestamp string, or a
 * Sheets serial number (what a hand-typed date becomes). Anything else is
 * undefined rather than a guess at MM/DD vs DD/MM.
 */
export function parseSheetDate(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = Math.round((value - SHEETS_EPOCH_OFFSET) * 86_400_000)
    return new Date(ms).toISOString().slice(0, 10)
  }
  if (typeof value !== 'string') return undefined
  const head = value.trim().slice(0, 10)
  return isIsoDate(head) ? head : undefined
}
